// El archipiélago y por dónde se puede caminar (T-001-06, prototipo).
//
// Módulo puro: sin React y sin Three, para poder probar las reglas del mundo
// sin WebGL.
//
// Las medidas salen de las cajas contenedoras reales de los GLB, no de
// tantear: `island_large` mide 6,27 × 6,30 (radio ≈ 3,13), su meseta
// caminable local está en y≈0,0435 (el máximo y≈0,245 es vegetación), y
// `bridge_straight` mide 1,63 de ancho por 2,13 de largo.
//
// El catálogo conserva diecinueve islas porque hay un solo modelo de isla en dos tamaños
// (`island_large` e `island_small`): se repite a distintas escalas y con
// distinto poblado. La lámina `isla-acuerdos-environment-sheet.png` tiene un
// archipiélago con más variedad; eso es trabajo de arte, no de código.
//
// --- Niveles ---
//
// Se pidió que ninguna isla quedara "más abajo" o "más arriba" sin que
// hubiera manera de subir o bajar andando: la respuesta no es una altura
// distinta por isla (eso se lee como un mapa caótico) sino tres niveles fijos
// y pocos:
//
//   NIVEL_0 = ALTURA_DEL_SUELO (0.2): la isla de partida y el primer anillo.
//   NIVEL_1 (1.4): el segundo anillo, donde están las dos islas con episodio.
//   NIVEL_2 (2.6): un tercer anillo, solo decorativo.
//
// Cada nivel sube 1.2 unidades sobre el anterior. Todos los cambios actuales
// de nivel son rampas continuas: la altura se interpola linealmente entre los
// dos extremos y `alturaDelSuelo` usa el mismo perfil que la malla visible.
//
// Regla dura: a las dos islas con episodio (`isla-acuerdos` → ep01,
// `isla-circulo` → ep02) se llega siempre andando, por puentes y rampas. Las
// piedras de paso —que sí dependen de saltar— nunca tocan una isla con
// episodio: llegar al contenido no puede depender de acertar un salto
// (Constitución V y VII). El runtime del MVP, sin embargo, activa solo EP01:
// Partida → Faro → Acuerdos. El resto del archipiélago se conserva como
// catálogo de reserva y no se instancia ni se puede recorrer en Capítulo 1.
// Lo comprueba `mundo.test.ts`.

/** Zona caminable circular: una isla. */
export interface IslaDelMundo {
  readonly clave: string;
  readonly centro: readonly [number, number];
  /** Modelo de isla elegido por arte, independiente del radio caminable. */
  readonly modelo: "island_large" | "island_small";
  /** Escala a la que se dibuja el modelo. */
  readonly escala: number;
  /** Radio por el que se puede andar, ya con margen respecto al borde. */
  readonly radioCaminable: number;
  /** Altura del suelo de esta isla. Ver "Niveles" arriba. */
  readonly altura: number;
  /**
   * Episodio que se juega en esta isla, si tiene uno. Es el `id` del registro
   * de `shared/episodios.ts`; aquí no se guarda ni un título ni una sola
   * palabra de contenido, que se leen de `content/` como todo lo demás.
   */
  readonly episodioId?: string;
  /**
   * Dónde hay que acercarse para que el episodio empiece: el claro de la
   * isla. Relativo al mundo, no a la isla, para que no haya dos sistemas de
   * coordenadas que mantener a la vez.
   */
  readonly puntoDeEncuentro?: readonly [number, number];
}

/**
 * Zona caminable rectangular alineada a los ejes: un puente, una rampa o una
 * escalera. Las tres son la misma forma de dato —un rectángulo con una
 * altura en cada extremo— porque las tres resuelven el mismo problema
 * (conectar dos orillas); lo único que cambia es cuánta pendiente tienen y,
 * por tanto, qué modelo 3D se usa para dibujarlas.
 */
export interface PuenteDelMundo {
  readonly clave: string;
  readonly centro: readonly [number, number];
  /** Eje por el que avanza el tablero; no se infiere de cuál semiextensión es mayor. */
  readonly eje: "x" | "z";
  /** Mitad del ancho en x y en z de la zona por la que se puede pasar. */
  readonly medioAncho: number;
  readonly medioLargo: number;
  /** Giro con el que se dibuja el modelo, en radianes. */
  readonly rotacionY: number;
  /** Islas en los extremos de coordenada menor y mayor, respectivamente. */
  readonly islasConectadas?: readonly [string, string];
  /**
   * Altura del suelo en cada extremo de la zona, medida a lo largo del eje
   * largo (declarado por `eje`). `alturaInicio` es el extremo de coordenada menor
   * (`centro - medio*`) y `alturaFin` el de coordenada mayor. Cuando los dos
   * valores coinciden el puente es plano, como todos los del primer anillo;
   * cuando difieren, `alturaDelSuelo` interpola en línea recta entre ambos.
   */
  readonly alturaInicio: number;
  readonly alturaFin: number;
  /**
   * Elemento de `environment` que puede dejar este puente intransitable. El
   * guion de EP01 dice `bridge_main: "broken"` en varias escenas y lo repara
   * más adelante: si el puente se sigue dibujando entero y se puede seguir
   * caminando por él, el guion miente. Mientras el estado esté en la lista,
   * ni `esCaminable` ni el dibujo lo cuentan como cruzable.
   */
  readonly dependeDe?: { readonly elemento: string; readonly estadosQueLoRompen: readonly string[] };
}

/** Semilongitud real del tablero según su eje declarado. */
export function semilargoDelPuente(puente: PuenteDelMundo): number {
  return puente.eje === "x" ? puente.medioAncho : puente.medioLargo;
}

/** Extremos geométricos en orden de coordenada menor → mayor. */
export function extremosDelPuente(puente: PuenteDelMundo): readonly [readonly [number, number], readonly [number, number]] {
  const [x, z] = puente.centro;
  const largo = semilargoDelPuente(puente);
  return puente.eje === "x"
    ? [[x - largo, z], [x + largo, z]]
    : [[x, z - largo], [x, z + largo]];
}

/** Altura del césped de una isla de partida, medida en el GLB. */
export const ALTURA_DEL_SUELO = 0.2;

/** Los tres niveles del archipiélago. Ver la nota "Niveles" al inicio del archivo. */
export const NIVEL_0 = ALTURA_DEL_SUELO;
export const NIVEL_1 = 1.4;
export const NIVEL_2 = 2.6;

/** Apertura global revisada junto con las huellas de las plataformas. */
export const ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO = 1.35;

/** Eje X/Z del corredor principal: las islas de cada brazo comparten carril. */
const Z_EJE_PRINCIPAL = 0;

/** Plataformas +13% de diámetro; ambos GLB conservan una huella equivalente. */
const ESCALA_ISLA_SECUNDARIA_GRANDE = 1.58;
const ESCALA_ISLA_SECUNDARIA_COMPACTA = 2.74;
/** Radio +16,25%: aproximadamente +35% de superficie caminable por isla. */
export const RADIO_CAMINABLE_ISLAS_SECUNDARIAS = 4.65;

/** Semiancho útil compartido por el tablero visible (1,63 m × 1,15 de escala). */
export const MEDIO_ANCHO_CAMINABLE_PUENTE = 0.93;

/** Lleva un punto del mapa de referencia a la distribución espacial vigente. */
export function puntoDeDistribucion(x: number, z: number): readonly [number, number] {
  return [x * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, z * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO];
}

/**
 * Isla de partida. No tiene episodio a propósito: es el sitio donde se
 * aparece y desde el que se sale a buscar. Poner una misión aquí hacía que lo
 * primero que se viera fuera un cartel pidiendo empezar.
 */
export const ISLA_PRINCIPAL: IslaDelMundo = {
  clave: "isla-partida",
  centro: [0, 0],
  modelo: "island_large",
  escala: 1.95,
  radioCaminable: 5.2,
  altura: NIVEL_0,
};

/**
 * El archipiélago. Diecinueve islas en tres niveles: el
 * primer anillo (NIVEL_0, a pie de la de partida), el segundo (NIVEL_1, con
 * las dos misiones) y un tercero (NIVEL_2), para que haya más mundo del que
 * uno necesita para llegar al contenido. La apertura espacial se aplica aquí
 * y a los anclajes del contenido con el mismo factor; las claves/conexiones
 * se conservan, no las coordenadas absolutas previas.
 */
export const ISLAS: readonly IslaDelMundo[] = [
  ISLA_PRINCIPAL,

  // --- Primer anillo (NIVEL_0): se ve desde la isla de partida ---
  { clave: "isla-faro", centro: puntoDeDistribucion(10, Z_EJE_PRINCIPAL), modelo: "island_large", escala: ESCALA_ISLA_SECUNDARIA_GRANDE, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_0 },
  { clave: "isla-palmeras", centro: puntoDeDistribucion(-9.375, Z_EJE_PRINCIPAL), modelo: "island_large", escala: ESCALA_ISLA_SECUNDARIA_GRANDE, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_0 },
  { clave: "isla-mirador", centro: puntoDeDistribucion(0, -10.75), modelo: "island_large", escala: ESCALA_ISLA_SECUNDARIA_GRANDE, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_0 },
  { clave: "isla-caleta", centro: puntoDeDistribucion(10, 8), modelo: "island_large", escala: ESCALA_ISLA_SECUNDARIA_GRANDE, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_0 },
  { clave: "isla-arenal", centro: puntoDeDistribucion(-9.375, 8), modelo: "island_large", escala: ESCALA_ISLA_SECUNDARIA_GRANDE, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_0 },
  { clave: "isla-lago", centro: puntoDeDistribucion(0, 10), modelo: "island_small", escala: ESCALA_ISLA_SECUNDARIA_COMPACTA, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_0 },

  // --- Segundo anillo (NIVEL_1): hay que cruzar el primero para llegar ---
  {
    clave: "isla-acuerdos",
    centro: puntoDeDistribucion(20.625, Z_EJE_PRINCIPAL),
    modelo: "island_large",
    escala: ESCALA_ISLA_SECUNDARIA_GRANDE,
    radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS,
    altura: NIVEL_1,
    episodioId: "ep01",
    // Conserva 1,464 m hacia el brazo sur respecto al nuevo eje z=0.
    puntoDeEncuentro: puntoDeDistribucion(20.625, 1.2),
  },
  {
    clave: "isla-circulo",
    centro: puntoDeDistribucion(0, -21.25),
    modelo: "island_large",
    escala: ESCALA_ISLA_SECUNDARIA_GRANDE,
    radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS,
    altura: NIVEL_1,
    episodioId: "ep02",
    puntoDeEncuentro: puntoDeDistribucion(0, -20.25),
  },
  { clave: "isla-piedra", centro: puntoDeDistribucion(-19.375, Z_EJE_PRINCIPAL), modelo: "island_large", escala: ESCALA_ISLA_SECUNDARIA_GRANDE, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_1 },
  { clave: "isla-nube", centro: puntoDeDistribucion(10, 16.25), modelo: "island_small", escala: ESCALA_ISLA_SECUNDARIA_COMPACTA, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_1 },
  { clave: "isla-jardin", centro: puntoDeDistribucion(-9.375, 16.875), modelo: "island_small", escala: ESCALA_ISLA_SECUNDARIA_COMPACTA, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_1 },
  { clave: "isla-bosque", centro: puntoDeDistribucion(20.625, 10), modelo: "island_large", escala: ESCALA_ISLA_SECUNDARIA_GRANDE, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_1 },
  // Se conserva un vano de 5,1 m a Duna para que el tercer tramo no quede
  // comprimido al enderezar su columna; el tablero largo ya es dinámico.
  { clave: "isla-duna", centro: puntoDeDistribucion(-19.375, 10.75), modelo: "island_large", escala: ESCALA_ISLA_SECUNDARIA_GRANDE, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_1 },

  // --- Tercer anillo (NIVEL_2): puramente decorativo ---
  { clave: "isla-cascada", centro: puntoDeDistribucion(29.5, Z_EJE_PRINCIPAL), modelo: "island_small", escala: ESCALA_ISLA_SECUNDARIA_COMPACTA, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_2 },
  { clave: "isla-risco", centro: puntoDeDistribucion(0, -31.25), modelo: "island_small", escala: ESCALA_ISLA_SECUNDARIA_COMPACTA, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_2 },
  { clave: "isla-cueva", centro: puntoDeDistribucion(-28.75, Z_EJE_PRINCIPAL), modelo: "island_large", escala: ESCALA_ISLA_SECUNDARIA_GRANDE, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_2 },
  { clave: "isla-nido", centro: puntoDeDistribucion(10, 25.625), modelo: "island_small", escala: ESCALA_ISLA_SECUNDARIA_COMPACTA, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_2 },
  { clave: "isla-sendero", centro: puntoDeDistribucion(-9.375, 25.625), modelo: "island_small", escala: ESCALA_ISLA_SECUNDARIA_COMPACTA, radioCaminable: RADIO_CAMINABLE_ISLAS_SECUNDARIAS, altura: NIVEL_2 },
];

/**
 * Los puentes están a la altura por la que se anda (z≈0,5, donde se paran los
 * personajes), no en la línea que une los centros de las islas: si no, había
 * que buscarlos, y caminar hacia la isla de al lado terminaba en el borde sin
 * explicación. `medioLargo` coincide con el ancho real del tablero (1,63 del
 * modelo por 1,15 de escala, la mitad) para que no se pueda andar por el
 * aire al lado del puente.
 *
 * `alturaInicio`/`alturaFin` van en el orden del eje largo: el extremo de
 * coordenada menor primero. Cuando las dos islas que conecta un puente están
 * en el mismo nivel, los dos valores son iguales (plano); cuando cambian de
 * nivel, se interpolan para la rampa.
 */
interface ConexionEntreIslas {
  readonly clave: string;
  readonly eje: "x" | "z";
  readonly islasConectadas: readonly [string, string];
  /** z si el tablero avanza por x; x si avanza por z. */
  readonly coordenadaTransversal: number;
  /** Semiancho de la zona caminable perpendicular al vano. */
  readonly medioTransversal: number;
  readonly dependeDe?: PuenteDelMundo["dependeDe"];
}

/**
 * Calcula el vano a partir del borde caminable real de ambas islas. La línea
 * transversal conserva el trazado elegido a mano; los extremos, longitud y
 * alturas dejan de ser números duplicados que se desajusten cuando crece una
 * plataforma. El nivel de cada extremo sale de la isla que toca ese lado, no
 * de asumir que la primera isla declarada queda en coordenada menor.
 */
function crearPuenteEntreIslas(conexion: ConexionEntreIslas): PuenteDelMundo {
  const [claveA, claveB] = conexion.islasConectadas;
  const islaA = ISLAS.find((isla) => isla.clave === claveA);
  const islaB = ISLAS.find((isla) => isla.clave === claveB);
  if (!islaA || !islaB) throw new Error(`${conexion.clave}: falta una isla conectada.`);

  const eje = conexion.eje === "x" ? 0 : 1;
  const transversal = 1 - eje;
  const coordenada = (isla: IslaDelMundo, indice: number) => isla.centro[indice]!;
  const extremoDe = (isla: IslaDelMundo, otra: IslaDelMundo) => {
    const diferenciaTransversal = conexion.coordenadaTransversal - coordenada(isla, transversal);
    // La orilla más larga que toca el ancho del tablero puede estar fuera del
    // eje central cuando este cruza la isla con un offset lateral. Ajustamos
    // el extremo al borde de esa franja, y el labio plano resuelve el resto.
    const desvioMasCercano = Math.max(0, Math.abs(diferenciaTransversal) - conexion.medioTransversal);
    const radioRestante = isla.radioCaminable ** 2 - desvioMasCercano ** 2;
    const direccion = Math.sign(coordenada(otra, eje) - coordenada(isla, eje));
    if (radioRestante <= 0 || direccion === 0) {
      throw new Error(`${conexion.clave}: el eje no cruza el borde de ${isla.clave}.`);
    }
    return {
      // Un micrón hacia dentro evita que `hypot` redondee un extremo exacto
      // apenas fuera del círculo y corte el primer paso del puente.
      posicion: coordenada(isla, eje) + direccion * (Math.sqrt(radioRestante) - 1e-6),
      isla,
    };
  };

  const [menor, mayor] = [extremoDe(islaA, islaB), extremoDe(islaB, islaA)]
    .sort((a, b) => a.posicion - b.posicion);
  if (!menor || !mayor || mayor.posicion <= menor.posicion) {
    throw new Error(`${conexion.clave}: las islas no forman un vano positivo.`);
  }
  const centroDelVano = (menor.posicion + mayor.posicion) / 2;
  const semilargo = (mayor.posicion - menor.posicion) / 2;
  const centro: readonly [number, number] = conexion.eje === "x"
    ? [centroDelVano, conexion.coordenadaTransversal]
    : [conexion.coordenadaTransversal, centroDelVano];

  return {
    clave: conexion.clave,
    centro,
    eje: conexion.eje,
    islasConectadas: conexion.islasConectadas,
    medioAncho: conexion.eje === "x" ? semilargo : conexion.medioTransversal,
    medioLargo: conexion.eje === "z" ? semilargo : conexion.medioTransversal,
    rotacionY: conexion.eje === "x" ? Math.PI / 2 : 0,
    alturaInicio: menor.isla.altura,
    alturaFin: mayor.isla.altura,
    ...(conexion.dependeDe ? { dependeDe: conexion.dependeDe } : {}),
  };
}

const CONEXIONES_ENTRE_ISLAS: readonly ConexionEntreIslas[] = [
  // Primer anillo: el primer puente de EP01 conecta Partida con Faro.
  { clave: "puente-este", eje: "x", islasConectadas: ["isla-partida", "isla-faro"], coordenadaTransversal: Z_EJE_PRINCIPAL * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE,
    dependeDe: { elemento: "bridge_main", estadosQueLoRompen: ["broken", "partially_fixed"] } },
  { clave: "puente-oeste", eje: "x", islasConectadas: ["isla-palmeras", "isla-partida"], coordenadaTransversal: Z_EJE_PRINCIPAL * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-mirador", eje: "z", islasConectadas: ["isla-mirador", "isla-partida"], coordenadaTransversal: 0, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-caleta", eje: "z", islasConectadas: ["isla-faro", "isla-caleta"], coordenadaTransversal: 10 * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-arenal", eje: "z", islasConectadas: ["isla-palmeras", "isla-arenal"], coordenadaTransversal: -9.375 * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-lago", eje: "z", islasConectadas: ["isla-partida", "isla-lago"], coordenadaTransversal: 0, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },

  // Hacia el segundo anillo. Este puente no participa en la reparación inicial de EP01.
  { clave: "puente-acuerdos", eje: "x", islasConectadas: ["isla-faro", "isla-acuerdos"], coordenadaTransversal: Z_EJE_PRINCIPAL * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-circulo", eje: "z", islasConectadas: ["isla-circulo", "isla-mirador"], coordenadaTransversal: 0, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-piedra", eje: "x", islasConectadas: ["isla-piedra", "isla-palmeras"], coordenadaTransversal: Z_EJE_PRINCIPAL * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-nube", eje: "z", islasConectadas: ["isla-caleta", "isla-nube"], coordenadaTransversal: 10 * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-jardin", eje: "z", islasConectadas: ["isla-arenal", "isla-jardin"], coordenadaTransversal: -9.375 * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },

  // Cruces del segundo anillo y subida al tercer nivel.
  { clave: "puente-duna", eje: "z", islasConectadas: ["isla-piedra", "isla-duna"], coordenadaTransversal: -19.375 * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-bosque", eje: "z", islasConectadas: ["isla-acuerdos", "isla-bosque"], coordenadaTransversal: 20.625 * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-cascada", eje: "x", islasConectadas: ["isla-acuerdos", "isla-cascada"], coordenadaTransversal: Z_EJE_PRINCIPAL * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-risco", eje: "z", islasConectadas: ["isla-risco", "isla-circulo"], coordenadaTransversal: 0, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-cueva", eje: "x", islasConectadas: ["isla-cueva", "isla-piedra"], coordenadaTransversal: Z_EJE_PRINCIPAL * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-nido", eje: "z", islasConectadas: ["isla-nube", "isla-nido"], coordenadaTransversal: 10 * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
  { clave: "puente-sendero", eje: "z", islasConectadas: ["isla-jardin", "isla-sendero"], coordenadaTransversal: -9.375 * ESCALA_DE_DISTRIBUCION_ARCHIPIELAGO, medioTransversal: MEDIO_ANCHO_CAMINABLE_PUENTE },
];

export const PUENTES: readonly PuenteDelMundo[] = CONEXIONES_ENTRE_ISLAS.map(crearPuenteEntreIslas);

/**
 * Subconjunto explícito que se muestra y se puede recorrer en el MVP de EP01.
 * El catálogo completo de islas/puentes se conserva arriba para expansiones,
 * pero no forma parte del espacio caminable de esta versión.
 */
const ISLAS_ACTIVAS_CAPITULO_1 = new Set(["isla-partida", "isla-faro", "isla-acuerdos"]);
const PUENTES_ACTIVOS_CAPITULO_1 = new Set(["puente-este", "puente-acuerdos"]);
export const ISLAS_CAPITULO_1: readonly IslaDelMundo[] = ISLAS.filter((isla) =>
  ISLAS_ACTIVAS_CAPITULO_1.has(isla.clave),
);
export const PUENTES_CAPITULO_1: readonly PuenteDelMundo[] = PUENTES.filter((puente) =>
  PUENTES_ACTIVOS_CAPITULO_1.has(puente.clave),
);

/**
 * Dónde aparece el personaje al abrir el juego.
 *
 * Está a propósito LEJOS de todos los claros. Empezaba justo encima del de la
 * isla principal, así que lo primero que veía quien jugaba era el cartel de
 * «empezar el episodio»: el mundo pedía entrar antes de dejar mirar. Ahora se
 * aparece en un sitio donde no pasa nada y hay que ir a buscar el punto.
 * `cercania.test.ts` vigila que siga siendo así.
 */
export const PUNTO_DE_PARTIDA: readonly [number, number] = [0.2, -1.9];

/** Labios lógicos de 32 cm en cada boca: corrigen el estrechamiento circular
 * de la isla en los laterales del tablero. El mesh de la plataforma ya se
 * solapa con el borde visible; estos apoyos mantienen continua la superficie
 * caminable por todo el ancho, incluso cuando el puente narrativo está roto. */
const MEDIO_LARGO_DE_DESEMBARCO = 0.16;
export const DESEMBARCOS: readonly PuenteDelMundo[] = PUENTES.flatMap((puente) => {
  const [extremoMenor, extremoMayor] = extremosDelPuente(puente);
  return [
    { extremo: extremoMenor, signo: -1, altura: puente.alturaInicio, lado: "menor" },
    { extremo: extremoMayor, signo: 1, altura: puente.alturaFin, lado: "mayor" },
  ].map(({ extremo, signo, altura, lado }) => {
    const centro: readonly [number, number] = puente.eje === "x"
      ? [extremo[0] + signo * MEDIO_LARGO_DE_DESEMBARCO, extremo[1]]
      : [extremo[0], extremo[1] + signo * MEDIO_LARGO_DE_DESEMBARCO];
    return {
      clave: `desembarco-${puente.clave}-${lado}`,
      centro,
      eje: puente.eje,
      medioAncho: puente.eje === "x" ? MEDIO_LARGO_DE_DESEMBARCO : puente.medioAncho,
      medioLargo: puente.eje === "z" ? MEDIO_LARGO_DE_DESEMBARCO : puente.medioLargo,
      rotacionY: puente.rotacionY,
      alturaInicio: altura,
      alturaFin: altura,
    };
  });
});

export const DESEMBARCOS_CAPITULO_1: readonly PuenteDelMundo[] = DESEMBARCOS.filter((desembarco) =>
  [...PUENTES_ACTIVOS_CAPITULO_1].some((clave) => desembarco.clave.startsWith(`desembarco-${clave}-`)),
);

const SUPERFICIES_RECTANGULARES = [...DESEMBARCOS, ...PUENTES];
const SUPERFICIES_RECTANGULARES_CAPITULO_1 = [...DESEMBARCOS_CAPITULO_1, ...PUENTES_CAPITULO_1];

/** Índice espacial para no recorrer los 54 labios/tableros en cada paso. */
const TAMANO_CELDA_SUPERFICIES = 4;
function indexarSuperficies(superficies: readonly PuenteDelMundo[]): ReadonlyMap<string, readonly PuenteDelMundo[]> {
  const porCelda = new Map<string, PuenteDelMundo[]>();
  for (const superficie of superficies) {
    const desdeX = Math.floor((superficie.centro[0] - superficie.medioAncho - 1e-9) / TAMANO_CELDA_SUPERFICIES);
    const hastaX = Math.floor((superficie.centro[0] + superficie.medioAncho + 1e-9) / TAMANO_CELDA_SUPERFICIES);
    const desdeZ = Math.floor((superficie.centro[1] - superficie.medioLargo - 1e-9) / TAMANO_CELDA_SUPERFICIES);
    const hastaZ = Math.floor((superficie.centro[1] + superficie.medioLargo + 1e-9) / TAMANO_CELDA_SUPERFICIES);
    for (let celdaX = desdeX; celdaX <= hastaX; celdaX += 1) {
      for (let celdaZ = desdeZ; celdaZ <= hastaZ; celdaZ += 1) {
        const clave = `${celdaX},${celdaZ}`;
        const elementos = porCelda.get(clave) ?? [];
        elementos.push(superficie);
        porCelda.set(clave, elementos);
      }
    }
  }
  return porCelda;
}

const superficiesPorCelda = indexarSuperficies(SUPERFICIES_RECTANGULARES);
const superficiesPorCeldaCapitulo1 = indexarSuperficies(SUPERFICIES_RECTANGULARES_CAPITULO_1);

export type AlcanceDeMundo = "archipielago" | "capitulo-1";

function superficiesCercanas(x: number, z: number, alcance: AlcanceDeMundo): readonly PuenteDelMundo[] {
  const celdaX = Math.floor(x / TAMANO_CELDA_SUPERFICIES);
  const celdaZ = Math.floor(z / TAMANO_CELDA_SUPERFICIES);
  const indice = alcance === "capitulo-1" ? superficiesPorCeldaCapitulo1 : superficiesPorCelda;
  return indice.get(`${celdaX},${celdaZ}`) ?? [];
}

/** Las islas donde empieza un episodio, en el orden en que están declaradas. */
export const ISLAS_CON_EPISODIO: readonly (IslaDelMundo & {
  readonly episodioId: string;
  readonly puntoDeEncuentro: readonly [number, number];
})[] = ISLAS.filter(
  (isla): isla is IslaDelMundo & { episodioId: string; puntoDeEncuentro: readonly [number, number] } =>
    isla.episodioId !== undefined && isla.puntoDeEncuentro !== undefined,
);

export const ISLAS_CON_EPISODIO_CAPITULO_1 = ISLAS_CAPITULO_1.filter(
  (isla): isla is IslaDelMundo & { episodioId: string; puntoDeEncuentro: readonly [number, number] } =>
    isla.episodioId !== undefined && isla.puntoDeEncuentro !== undefined,
);

/**
 * Piedra de paso: un islote pequeño al que se llega saltando.
 *
 * Son la otra forma de cruzar, además de los puentes, y existen solo entre
 * islas SIN misión. Eso no es un detalle: llegar a un episodio no puede
 * depender de acertar un salto, porque entonces el contenido quedaría detrás
 * de una habilidad y quien no la tuviera se quedaría fuera (Constitución V y
 * VII). A las dos islas con episodio se llega andando por puentes y rampas,
 * siempre.
 *
 * Se dibujan con `rock_small`/`rock_large`. El tamaño de cada roca sigue
 * viniendo de `radioCaminable`, para que lo que se ve y lo que se pisa no se
 * separen. `altura` es la del punto exacto donde se aterriza; en un camino
 * que sube de nivel (como el del oeste) cada piedra tiene la suya, a modo de
 * peldaños sueltos, pero eso es cosmético: la ruta garantizada a pie a las
 * islas con episodio nunca pasa por aquí.
 */
export interface PiedraDePaso {
  readonly clave: string;
  readonly centro: readonly [number, number];
  readonly radioCaminable: number;
  readonly altura: number;
}

/**
 * El atajo opcional de cuatro saltos entre la isla de partida y la caleta
 * (al mismo nivel). Los huecos se calculan contra las constantes reales
 * de `salto.ts` en `mundo.test.ts`, así que si alguien toca la física del
 * salto y los huecos dejan de ser saltables, el test lo dice.
 */
export const PIEDRAS: readonly PiedraDePaso[] = [
  { clave: "piedra-caleta-1", centro: puntoDeDistribucion(3.6, 3.0), radioCaminable: 0.75, altura: NIVEL_0 },
  { clave: "piedra-caleta-2", centro: puntoDeDistribucion(4.8, 4.1), radioCaminable: 0.7, altura: NIVEL_0 },
  { clave: "piedra-caleta-3", centro: puntoDeDistribucion(6.0, 5.1), radioCaminable: 0.7, altura: NIVEL_0 },
  // El antiguo salto paralelo del oeste se retira del montaje: ya existe un
  // puente accesible en esa conexión y las piedras caían dentro de su tablero.
  // Los GLB no se borran; solo se evita dibujar ese segundo trayecto redundante.
];
/** En EP01 no hay atajos saltables: el recorrido entero se completa andando. */
export const PIEDRAS_CAPITULO_1: readonly PiedraDePaso[] = [];

/** Largo de un tablero de puente ya escalado, para repartir las piezas. */
export const LARGO_DE_TABLERO = 2.13 * 1.15;

/** Escala fija con la que se dibuja cada tablero de puente. */
export const ESCALA_DE_TABLERO = 1.15;

/**
 * Caras superiores medidas en los GLB v002. El camino se dibuja con su plano
 * pisable 5,3 cm sobre la altura lógica de la isla; el tablero debe coincidir
 * con ese mismo plano para no crear un pequeño escalón en cada desembarco.
 */
export const ALTURA_SUPERFICIE_CAMINO_GLB = 0.193;
// Cara pisable medida en el GLB v002 (no confundir con el sendero, ~0,193 m).
export const ALTURA_SUPERFICIE_TABLERO_GLB = 0.10504;
const ALTURA_ORIGEN_CAMINO_NIVEL_0 = 0.06;
export const ELEVACION_VISUAL_CAMINO =
  ALTURA_ORIGEN_CAMINO_NIVEL_0 - ALTURA_DEL_SUELO + ALTURA_SUPERFICIE_CAMINO_GLB;

/** Posición Y del origen del GLB de camino para que su cara superior coincida. */
export function posicionYDeSendero(alturaDelSuelo: number): number {
  return alturaDelSuelo + ELEVACION_VISUAL_CAMINO - ALTURA_SUPERFICIE_CAMINO_GLB;
}

/**
 * Desfase local del GLB de puente respecto a la altura central del tablero.
 * En rampas compensa también la rotación de su eje Y, manteniendo la cara
 * superior central en el mismo plano que los senderos.
 */
export function desfaseVerticalDelTablero(inclinacion = 0): number {
  const coseno = Math.cos(inclinacion);
  if (coseno <= 0) throw new Error("La inclinación del tablero debe ser menor que 90°.");
  return ELEVACION_VISUAL_CAMINO / coseno - ALTURA_SUPERFICIE_TABLERO_GLB * ESCALA_DE_TABLERO;
}

/**
 * Cuántos tableros hacen falta para cubrir un puente entero, y dónde va cada
 * uno a lo largo del eje por el que se cruza.
 *
 * Antes había siempre dos tableros repartidos "a ojo" a media distancia del
 * centro: con un vano corto sobraba hueco entre ellos (se veían dos trozos
 * con una junta en medio) y con uno largo no llegaban a las dos orillas. Acá
 * el número de piezas sale del vano real: se calcula cuántos tableros de
 * `LARGO_DE_TABLERO` hacen falta para cubrirlo (`Math.ceil`) y se reparten a
 * distancia igual entre sí, de modo que el paso entre centros nunca sea mayor
 * que `LARGO_DE_TABLERO` y no quede un hueco visible. Si el vano cambia en
 * `PUENTES`, el número de piezas cambia solo: no hay que retocar el dibujo.
 *
 * Devuelve las distancias (offsets) al centro del puente a lo largo de su eje
 * largo; quien dibuja solo tiene que sumar cada offset a la coordenada que
 * corresponda según `rotacionY`.
 */
export function offsetsDeTablerosDePuente(puente: PuenteDelMundo): readonly number[] {
  const vano = semilargoDelPuente(puente) * 2;
  const cantidad = Math.max(1, Math.ceil(vano / LARGO_DE_TABLERO));
  const paso = vano / cantidad;
  return Array.from({ length: cantidad }, (_, indice) => -vano / 2 + paso * (indice + 0.5));
}

interface Punto {
  readonly x: number;
  readonly z: number;
}

function distancia(a: Punto, b: Punto): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/**
 * Un pelo hacia dentro del borde. Proyectar justo sobre el radio deja el
 * punto fuera por el error de la coma flotante, y entonces el sitio al que se
 * acaba de mandar al personaje resulta no ser caminable. Lo cazaron los
 * tests, no una revisión.
 */
const MARGEN_DE_BORDE = 1e-6;

/** Vale igual para una isla y para una piedra: las dos son círculos. */
function puntoMasCercanoEnIsla(
  isla: { readonly centro: readonly [number, number]; readonly radioCaminable: number },
  punto: Punto,
): Punto {
  const [cx, cz] = isla.centro;
  const dx = punto.x - cx;
  const dz = punto.z - cz;
  const largo = Math.hypot(dx, dz);
  if (largo <= isla.radioCaminable) return punto;
  const factor = (isla.radioCaminable - MARGEN_DE_BORDE) / largo;
  return { x: cx + dx * factor, z: cz + dz * factor };
}

function puntoMasCercanoEnPuente(puente: PuenteDelMundo, punto: Punto): Punto {
  const [cx, cz] = puente.centro;
  // Proyectar ligeramente dentro: sumar centro + semiancho y restarlo en
  // esCaminable puede redondear hacia fuera, especialmente en desembarcos.
  const ancho = Math.max(0, puente.medioAncho - 1e-7);
  const largo = Math.max(0, puente.medioLargo - 1e-7);
  return {
    x: Math.min(Math.max(punto.x, cx - ancho), cx + ancho),
    z: Math.min(Math.max(punto.z, cz - largo), cz + largo),
  };
}

/**
 * Un puente roto no se puede cruzar. El estado sale del `environment` de la
 * escena actual (el mismo objeto que ya decide qué props se dibujan); sin
 * argumento se asume que ningún puente está roto, que es el caso de siempre
 * fuera de EP01 y lo que ya esperaban los llamadores existentes.
 */
function puenteEstaRoto(
  puente: PuenteDelMundo,
  environment: Readonly<Record<string, string>>,
): boolean {
  if (!puente.dependeDe) return false;
  const estado = environment[puente.dependeDe.elemento];
  return estado !== undefined && puente.dependeDe.estadosQueLoRompen.includes(estado);
}

const SIN_ENTORNO: Readonly<Record<string, string>> = {};

/** `true` si se puede estar de pie en ese punto: isla, puente o piedra. */
export function esCaminable(
  x: number,
  z: number,
  environment: Readonly<Record<string, string>> = SIN_ENTORNO,
  alcance: AlcanceDeMundo = "archipielago",
): boolean {
  const islas = alcance === "capitulo-1" ? ISLAS_CAPITULO_1 : ISLAS;
  const piedras = alcance === "capitulo-1" ? PIEDRAS_CAPITULO_1 : PIEDRAS;
  const enIsla = islas.some((isla) => {
    const dx = x - isla.centro[0];
    const dz = z - isla.centro[1];
    return dx * dx + dz * dz <= isla.radioCaminable ** 2;
  });
  if (enIsla) return true;
  const enPiedra = piedras.some(
    (piedra) => {
      const dx = x - piedra.centro[0];
      const dz = z - piedra.centro[1];
      return dx * dx + dz * dz <= piedra.radioCaminable ** 2;
    },
  );
  if (enPiedra) return true;
  return superficiesCercanas(x, z, alcance).some(
    (puente) =>
      Math.abs(x - puente.centro[0]) <= puente.medioAncho + 1e-9 &&
      Math.abs(z - puente.centro[1]) <= puente.medioLargo + 1e-9 &&
      !puenteEstaRoto(puente, environment),
  );
}

/**
 * Altura del suelo en ese punto, o `null` si ahí no se puede estar.
 *
 * Usa siempre el estado "sin puentes rotos", igual que `esCaminable` cuando
 * se la llama sin `environment`: esta función no recibe ese argumento a
 * propósito (otro módulo escribe contra esta firma exacta) y el caso de un
 * puente roto ya lo decide `esCaminable` antes de dejar caminar hasta ahí.
 *
 * En una isla o una piedra la altura es un valor fijo. En un puente (llano,
 * rampa o escalera) se interpola en línea recta entre `alturaInicio` y
 * `alturaFin` a lo largo de su eje largo, así que el paso de un nivel a otro
 * es continuo: no hay un escalón brusco que teletransporte al personaje.
 */
export function alturaDelSuelo(x: number, z: number, alcance: AlcanceDeMundo = "archipielago"): number | null {
  const islas = alcance === "capitulo-1" ? ISLAS_CAPITULO_1 : ISLAS;
  const piedras = alcance === "capitulo-1" ? PIEDRAS_CAPITULO_1 : PIEDRAS;
  for (const isla of islas) {
    const dx = x - isla.centro[0];
    const dz = z - isla.centro[1];
    if (dx * dx + dz * dz <= isla.radioCaminable ** 2) {
      return isla.altura;
    }
  }
  for (const piedra of piedras) {
    const dx = x - piedra.centro[0];
    const dz = z - piedra.centro[1];
    if (dx * dx + dz * dz <= piedra.radioCaminable ** 2) {
      return piedra.altura;
    }
  }
  for (const puente of superficiesCercanas(x, z, alcance)) {
    if (Math.abs(x - puente.centro[0]) > puente.medioAncho + 1e-9 || Math.abs(z - puente.centro[1]) > puente.medioLargo + 1e-9) {
      continue;
    }
    if (puenteEstaRoto(puente, SIN_ENTORNO)) continue;
    const enX = puente.eje === "x";
    const [cx, cz] = puente.centro;
    const medioLargo = semilargoDelPuente(puente);
    const t = enX
      ? (x - (cx - medioLargo)) / (2 * medioLargo)
      : (z - (cz - medioLargo)) / (2 * medioLargo);
    const tAcotado = Math.min(1, Math.max(0, t));
    return puente.alturaInicio + (puente.alturaFin - puente.alturaInicio) * tAcotado;
  }
  return null;
}

/**
 * El punto caminable más cercano al pedido. Se usa en dos sitios: para que
 * tocar el agua lleve a la orilla en vez de no hacer nada, y para que el paso
 * de cada cuadro no termine en el mar.
 */
export function acercarAZonaCaminable(
  x: number,
  z: number,
  environment: Readonly<Record<string, string>> = SIN_ENTORNO,
  alcance: AlcanceDeMundo = "archipielago",
): Punto {
  const punto = { x, z };
  const islas = alcance === "capitulo-1" ? ISLAS_CAPITULO_1 : ISLAS;
  const piedras = alcance === "capitulo-1" ? PIEDRAS_CAPITULO_1 : PIEDRAS;
  const superficies = alcance === "capitulo-1" ? SUPERFICIES_RECTANGULARES_CAPITULO_1 : SUPERFICIES_RECTANGULARES;
  if (esCaminable(x, z, environment, alcance)) return punto;

  const candidatos: Punto[] = [
    ...islas.map((isla) => puntoMasCercanoEnIsla(isla, punto)),
    ...piedras.map((piedra) => puntoMasCercanoEnIsla(piedra, punto)),
    ...superficies.filter((puente) => !puenteEstaRoto(puente, environment)).map((puente) =>
      puntoMasCercanoEnPuente(puente, punto),
    ),
  ];

  let mejor = candidatos[0] ?? punto;
  for (const candidato of candidatos) {
    if (distancia(candidato, punto) < distancia(mejor, punto)) mejor = candidato;
  }
  return mejor;
}
